import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getSessionHistory, deleteSession } from '../lib/firestore';
import { calculateTier } from '../shared';
import type { SessionStats } from '../shared';
import { Panel, EmptyState, LoadingSpinner } from '../components/ui/Cards';
import { Button } from '../components/ui/Button';

export function HistoryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<(SessionStats & { id: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    getSessionHistory(user.uid, 50).then((s: any[]) => {
      setSessions(s);
      setLoading(false);
    });
  }, [user]);

  const handleDelete = async (sessionId: string) => {
    if (!user) return;
    setDeleting(sessionId);
    await deleteSession(sessionId, user.uid);
    setSessions(prev => prev.filter(s => s.id !== sessionId));
    setConfirmDelete(null);
    setDeleting(null);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="page u-fade-up">
      <div className="page__header">
        <div>
          <h1 className="page__title">Session History</h1>
          <p className="page__sub">{sessions.length} session{sessions.length !== 1 ? 's' : ''} logged</p>
        </div>
        <Button onClick={() => navigate('/session')}>+ New Session</Button>
      </div>

      {sessions.length === 0 ? (
        <EmptyState
          icon=""
          title="No sessions yet"
          subtitle="Your session history will appear here once you've logged your first shots."
          action={<Button onClick={() => navigate('/session')}>Log First Session</Button>}
        />
      ) : (
        <Panel>
          {sessions.map((session) => {
            const tier = calculateTier(session.avgBreak, session.potRate, session.safetyRate);
            const date = session.date
              ? new Date((session.date as any).seconds * 1000)
              : new Date();
            const isConfirming = confirmDelete === session.id;
            const isDeleting   = deleting === session.id;

            if (isConfirming) {
              return (
                <div key={session.id} className="delete-confirm">
                  <span className="delete-confirm__text">
                    Delete this session? This cannot be undone.
                  </span>
                  <button
                    className="delete-confirm__yes"
                    onClick={() => handleDelete(session.id)}
                    disabled={isDeleting}
                  >
                    {isDeleting ? '…' : 'Delete'}
                  </button>
                  <button
                    className="delete-confirm__no"
                    onClick={() => setConfirmDelete(null)}
                  >
                    Cancel
                  </button>
                </div>
              );
            }

            return (
              <div key={session.id} className="history-row">
                {/* Date + type */}
                <div>
                  <div className="history-row__date">
                    {date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  </div>
                  <div className="history-row__type">{(session as any).sessionType ?? 'practice'}</div>
                </div>

                {/* Tier dot */}
                <div>
                  <div className="history-row__dot" style={{ background: tier.color }} />
                </div>

                {/* Stats */}
                <StatCell label="Pot %"      value={`${session.potRate}%`} />
                <StatCell label="High Break" value={session.highBreak} highlight />
                <StatCell label="Avg Break"  value={session.avgBreak} />
                <StatCell label="Safety"     value={`${session.safetyRate}%`} />

                {/* Delete button */}
                <button
                  className="history-row__delete"
                  onClick={() => setConfirmDelete(session.id)}
                  title="Delete session"
                >
                  ✕
                </button>
              </div>
            );
          })}
        </Panel>
      )}
    </div>
  );
}

function StatCell({ label, value, highlight }: {
  label: string; value: string | number; highlight?: boolean;
}) {
  return (
    <div className="stat-cell">
      <div className="stat-cell__val" style={{ color: highlight ? 'var(--gold)' : 'var(--chalk)' }}>
        {value}
      </div>
      <div className="stat-cell__label">{label}</div>
    </div>
  );
}